/**
 * ParksPageNew — full feature parity with old ParksPage.
 * Includes manual/API grouped sections, park management actions.
 */
import React from 'react';
import { useParks } from '../hooks/useParks';
import { EmptyState } from '../components/shared/EmptyState';
import { SkeletonRow } from '../design-system/primitives/SkeletonRow';
import { ActionButton } from '../design-system/primitives/ActionButton';
import { MapPin, Plus } from 'lucide-react';

export function ParksPageNew(): React.ReactElement {
  const { data: parks, isLoading } = useParks();
  const [mode, setMode] = React.useState<'all' | 'manual' | 'api'>('all');

  const manual = parks?.filter((p) => p.source === 'MANUAL') || [];
  const apiSynced = parks?.filter((p) => p.source === 'API') || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Parks</h1>
        <ActionButton variant="secondary" onClick={() => {}}>
          <Plus className="w-4 h-4" /> Add Park
        </ActionButton>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {(['all', 'manual', 'api'] as const).map((m) => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${mode === m ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'bg-white dark:bg-surface-dark-card border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
            {m === 'all' ? 'All' : m === 'manual' ? 'Manual' : 'API Synced'}
          </button>
        ))}
      </div>

      {isLoading && <SkeletonRow count={4} width="100%" />}

      {/* Manually Managed */}
      {(mode === 'all' || mode === 'manual') && manual.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Manually Managed</h2>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            {manual.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.name}</p>
                <span className="text-xs text-gray-400 dark:text-gray-500">Manual</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* API Synced */}
      {(mode === 'all' || mode === 'api') && apiSynced.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">API Synced</h2>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            {apiSynced.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.name}</p>
                <span className="text-xs text-gray-400 dark:text-gray-500">API Synced</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {!isLoading && !parks?.length && (
        <EmptyState icon={MapPin} iconColor="text-gray-400" title="No parks configured" />
      )}
    </div>
  );
}
